import React from "react";
import { Link } from "react-router-dom";
import ReactTimeago from "react-timeago";
import { apiServices } from "./_services/api.services";

const PAGE_SIZE = 10;

export class MembershipDemotion extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            candidates: [],
            cursor: 0,
            scanned: 0,
            errors: 0,
            errorDetails: [],
            exhausted: false,
            loading: false,
            error: null,
            queueSize: null,
            stripping: {},
            outcomes: {},
        };
        this.load = this.load.bind(this);
        this.reset = this.reset.bind(this);
    }

    componentDidMount() {
        this.loadQueueSize();
    }

    async loadQueueSize() {
        try {
            const resp = await apiServices.axios.get(`/membership/demotion-queue-size.json`);
            this.setState({ queueSize: resp.data.queue_size });
        } catch (e) {
            // Informational only — never block the page on the count
        }
    }

    async load() {
        this.setState({ loading: true, error: null });

        try {
            const resp = await apiServices.axios.get(`/membership/demotion-candidates.json?limit=${PAGE_SIZE}&after=${this.state.cursor}`);
            const { candidates, cursor, scanned, errors, error_details, exhausted } = resp.data;

            this.setState(prev => ({
                candidates: [...prev.candidates, ...candidates],
                cursor,
                scanned: prev.scanned + scanned,
                errors: prev.errors + errors,
                errorDetails: [...prev.errorDetails, ...(error_details || [])],
                exhausted,
                loading: false,
            }));
        } catch (e) {
            const msg = (e.response && e.response.data && e.response.data.error) || e.message || "Unknown error";
            this.setState({ error: msg, loading: false });
        }
    }

    reset() {
        this.setState({
            candidates: [],
            cursor: 0,
            scanned: 0,
            errors: 0,
            errorDetails: [],
            exhausted: false,
            error: null,
            stripping: {},
            outcomes: {},
        });
    }

    async strip(candidate) {
        this.setState(prev => ({ stripping: { ...prev.stripping, [candidate.id]: true } }));

        try {
            const resp = await apiServices.axios.post(`/characters/${candidate.id}/membership/strip`);
            this.setState(prev => ({
                stripping: { ...prev.stripping, [candidate.id]: false },
                outcomes: { ...prev.outcomes, [candidate.id]: resp.data },
                queueSize: resp.data.membership_removed && prev.queueSize !== null ? prev.queueSize - 1 : prev.queueSize,
            }));
        } catch (e) {
            const msg = (e.response && e.response.data && e.response.data.error) || e.message || "Unknown error";
            this.setState(prev => ({
                stripping: { ...prev.stripping, [candidate.id]: false },
                outcomes: { ...prev.outcomes, [candidate.id]: { error: msg } },
            }));
        }
    }

    renderColor(color) {
        if (!color) return <em className="tx-color-03">none</em>;

        return (
            <span>
                <span style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    borderRadius: "2px",
                    backgroundColor: `#${color}`,
                    marginRight: "6px",
                    verticalAlign: "middle",
                }} />
                <code>{color}</code>
            </span>
        );
    }

    renderPatreon(tier) {
        if (tier === 1) return <span className="badge badge-warning">Patron</span>;
        if (tier === 2) return <span className="badge badge-info">Cursed</span>;
        return <em className="tx-color-03">&mdash;</em>;
    }

    renderRow(c) {
        const { stripping, outcomes } = this.state;
        const outcome = outcomes[c.id];
        const isStripping = stripping[c.id];

        return (
            <tr key={c.id}>
                <td>
                    {c.online && (
                        <span
                            title="Online right now"
                            style={{
                                display: "inline-block",
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: "#28a745",
                                marginRight: "6px",
                                verticalAlign: "middle",
                            }}
                        />
                    )}
                    <Link to={`/characters/${c.id}`}>{c.name}</Link>
                    <span className="tx-color-03 tx-12 mg-l-5">#{c.id}</span>
                </td>
                <td>{c.platform === "ios" ? "iOS" : "Android"}</td>
                <td className="tx-nowrap">{c.expires > 1 ? <ReactTimeago date={c.expires} /> : "long gone"}</td>
                <td>{c.auto_renew === null ? <em className="tx-color-03">unknown</em> : c.auto_renew ? "yes" : "no"}</td>
                <td className="tx-nowrap">{c.last_login || c.last_online ? <ReactTimeago date={c.last_login || c.last_online} /> : "—"}</td>
                <td>{this.renderPatreon(c.patreon_tier)}</td>
                <td>{this.renderColor(c.name_color)}</td>
                <td>
                    {outcome ? (
                        outcome.error ? (
                            <span className="tx-danger tx-12">{outcome.error}</span>
                        ) : (
                            <span className="tx-success tx-12">
                                Stripped{outcome.color_cleared ? " + color cleared" : ""}
                            </span>
                        )
                    ) : (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={isStripping}
                            onClick={() => this.strip(c)}
                        >
                            {isStripping ? "Stripping..." : "Strip"}
                        </button>
                    )}
                </td>
            </tr>
        );
    }

    render() {
        const { candidates, exhausted, loading, error, scanned, errors, errorDetails, queueSize } = this.state;
        const started = scanned > 0 || candidates.length > 0;

        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <h4 className="mg-b-10">Membership Demotion Queue</h4>
                    <p className="tx-color-03 tx-13">
                        Lists members whose store subscription has lapsed (same condition as the disabled auto-demotion:
                        past the 2-day grace, and auto-renew off or expired for a week+).
                        Members without a store purchase reference are never listed.
                        Candidates are sorted by most recently active first (from the character's last save).
                        Stripping clears the <code>members</code> flag and the <code>ffa500</code> name color only —
                        the purchase token and iOS flag stay intact for reactivation.
                    </p>

                    <div className="mg-b-20">
                        <button className="btn btn-primary" onClick={this.load} disabled={loading || exhausted}>
                            {loading ? "Scanning..." : started ? `Load next ${PAGE_SIZE}` : "Load candidates"}
                        </button>
                        {started && (
                            <button className="btn btn-outline-primary mg-l-10" onClick={this.reset} disabled={loading}>
                                Reset
                            </button>
                        )}
                        {queueSize !== null && (
                            <span className="tx-color-03 tx-12 mg-l-15">
                                {queueSize} member(s) in queue
                            </span>
                        )}
                        {started && (
                            <span className="tx-color-03 tx-12 mg-l-15">
                                {scanned} member(s) verified against the stores{errors > 0 ? `, ${errors} verification error(s)` : ""}
                                {exhausted ? " — no more members to scan" : ""}
                            </span>
                        )}
                    </div>

                    {error && (
                        <div className="alert alert-danger">
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {errorDetails.length > 0 && (
                        <div className="alert alert-warning">
                            <strong>{errorDetails.length} verification error(s)</strong> — these members were skipped:
                            <ul className="mg-b-0 mg-t-5">
                                {errorDetails.map((e, i) => (
                                    <li key={i} className="tx-12">
                                        <Link to={`/characters/${e.id}`}>{e.name}</Link>
                                        <span className="tx-color-03"> #{e.id} [{e.platform === "ios" ? "iOS" : "Android"}]</span> — {e.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {candidates.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-striped table-sm">
                                <thead>
                                    <tr>
                                        <th>Player</th>
                                        <th>Platform</th>
                                        <th>Expired</th>
                                        <th>Auto-renew</th>
                                        <th>Last active</th>
                                        <th>Patreon</th>
                                        <th>Name color</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {candidates.map(c => this.renderRow(c))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {started && !loading && candidates.length === 0 && !error && (
                        <div className="alert alert-success">Nobody qualifies for demotion right now.</div>
                    )}
                </div>
            </div>
        );
    }
}
